// 관리자 공지/이벤트 발송 화면.
// 작업계획서 5.6: 전체 공지/이벤트 작성·발송 → 사용자 알림센터 + 푸시.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { adminBroadcastInputSchema } from "@oo/shared";
import { Button, useConfirm } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import { ApiError } from "@/api/client.ts";
import { broadcastNotification } from "@/features/notifications/notificationsApi.ts";

// 공지 전체발송 관리 화면.
export default function NotificationsPage() {
  const confirm = useConfirm();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  // 조용히 발송(개선항목 10): 앱 내 알림센터에만 남기고 푸시 알림은 보내지 않는다.
  const [silent, setSilent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: broadcastNotification,
    onSuccess: (recipients) => {
      setResult(`${recipients.toLocaleString()}명에게 발송했습니다.`);
      setError(null);
      setTitle("");
      setBody("");
      setSilent(false);
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError ? err.message : "발송에 실패했습니다.");
    },
  });

  async function onSend() {
    const parsed = adminBroadcastInputSchema.safeParse({ title, body, silent });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "입력값을 확인해 주세요.");
      return;
    }
    const ok = await confirm({
      title: "전체 발송",
      description: silent
        ? "전체 사용자에게 발송할까요? 푸시 없이 앱 내 알림에만 표시됩니다."
        : "전체 사용자에게 발송할까요? 푸시 알림도 함께 전송됩니다.",
      confirmLabel: "발송",
      tone: "brand",
    });
    if (!ok) {
      return;
    }
    mutation.mutate(parsed.data);
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="공지 / 이벤트 발송"
        description="작성한 공지는 전체 회원의 알림센터에 기록되고, 알림을 켠 사용자에게 푸시로 전송됩니다."
      />

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink-700">제목</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={60}
          placeholder="예: 설 연휴 휴무 안내"
          className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-ink-900 placeholder:text-ink-500"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-ink-700">내용</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={1000}
          rows={5}
          placeholder="공지/이벤트 내용을 입력하세요."
          className="rounded border border-ink-300 bg-surface p-3 text-ink-900 placeholder:text-ink-500"
        />
      </label>

      {/* 조용히 발송 옵션 */}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={silent}
          onChange={(e) => setSilent(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-ink-700">조용히 발송</span>
          <span className="block text-xs text-ink-600">
            푸시 알림 없이 앱 내 알림센터에만 표시합니다.
          </span>
        </span>
      </label>

      {error ? <p className="text-sm font-medium text-danger-700">{error}</p> : null}
      {result ? (
        <p className="rounded-md border border-success-600 bg-success-50 px-3 py-2 text-sm text-success-700">
          {result}
        </p>
      ) : null}

      <Button type="button" onClick={onSend} loading={mutation.isPending} className="self-start">
        {mutation.isPending ? "발송 중..." : "전체 발송"}
      </Button>
    </div>
  );
}
